import React, { useEffect, useState } from "react";
import { X, Plus, Loader2, ListMusic } from "lucide-react";
import { musicClient } from "@/lib/musicApi";
import { useToast } from "@/contexts/ToastContext";
import { useTranslation } from "react-i18next";

// Shared "Add to playlist" picker: the subscriber's own playlists + inline create.
export const AddToPlaylistModal = ({ trackId, onClose }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [lists, setLists] = useState(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    musicClient.get("/playlists/mine").then((r) => setLists(r.data || [])).catch(() => setLists([]));
  }, []);

  const addTo = async (pid) => {
    setBusy(true);
    try { await musicClient.patch(`/playlists/${pid}/tracks`, { add: [trackId] }); toast.success(t("playlist.added")); onClose(); }
    catch { toast.error(t("playlist.addError")); setBusy(false); }
  };
  const createAndAdd = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try { await musicClient.post("/playlists", { title: name.trim(), track_ids: [trackId] }); toast.success(t("playlist.created")); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("playlist.createError")); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[10012] flex items-end sm:items-center justify-center p-4" style={{ background: "rgba(30,27,75,0.55)" }} onClick={onClose} data-testid="add-to-playlist-modal">
      <div className="w-full max-w-sm rounded-2xl p-5 max-h-[70vh] overflow-y-auto" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-color)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold" style={{ color: "var(--text-primary)" }}>{t("playlist.title")}</h3>
          <button onClick={onClose} data-testid="atp-close" style={{ color: "var(--text-secondary)" }}><X size={18} /></button>
        </div>

        {creating ? (
          <div className="space-y-2">
            <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={t("playlist.namePlaceholder")} data-testid="atp-new-name" className="w-full px-3 py-2 rounded-lg text-sm outline-none" style={{ background: "var(--bg-subtle)", border: "1px solid var(--border-color)", color: "var(--text-primary)" }} />
            <div className="flex gap-2">
              <button onClick={() => setCreating(false)} className="flex-1 py-2 rounded-full text-sm" style={{ border: "1px solid var(--border-color)", color: "var(--text-primary)" }}>{t("playlist.back")}</button>
              <button onClick={createAndAdd} disabled={busy || !name.trim()} data-testid="atp-create-confirm" className="flex-1 py-2 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-1.5" style={{ background: "var(--accent-primary)", color: "#fff", opacity: busy || !name.trim() ? 0.6 : 1 }}>{busy ? <Loader2 size={14} className="animate-spin" /> : null} {t("playlist.create")}</button>
            </div>
          </div>
        ) : (
          <>
            <button onClick={() => setCreating(true)} data-testid="atp-new" className="flex items-center gap-2 w-full px-3 py-2.5 rounded-xl text-sm font-medium mb-2" style={{ background: "var(--accent-light, var(--bg-subtle))", color: "var(--accent-primary)" }}>
              <Plus size={16} /> {t("playlist.new")}
            </button>
            {lists === null ? (
              <p className="text-sm text-center py-6" style={{ color: "var(--text-secondary)" }}>{t("playlist.loading")}</p>
            ) : lists.length === 0 ? (
              <p className="text-xs text-center py-4" style={{ color: "var(--text-secondary)" }}>{t("playlist.empty")}</p>
            ) : (
              <div className="space-y-1">
                {lists.map((pl) => (
                  <button key={pl.id} onClick={() => addTo(pl.id)} disabled={busy} data-testid={`atp-list-${pl.id}`} className="flex items-center gap-3 w-full px-3 py-2 rounded-xl text-left hover:bg-[color:var(--bg-subtle)]">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "var(--bg-subtle)" }}>{pl.cover ? <img src={pl.cover} alt="" className="w-full h-full object-cover rounded-lg" /> : <ListMusic size={16} style={{ color: "var(--text-secondary)" }} />}</div>
                    <div className="min-w-0"><p className="text-sm font-medium truncate" style={{ color: "var(--text-primary)" }}>{pl.title}</p><p className="text-xs" style={{ color: "var(--text-secondary)" }}>{t("playlist.trackCount", { count: pl.track_count })}</p></div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AddToPlaylistModal;
